// frontend/src/widgets/AdvancedCustomerListWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-07-09
// Updated:      2026-09-07 13:25 BST
//
// Description:
//   Advanced customer list with column chooser, drag/drop reordering,
//   per-user/tenant persistence, and clean API usage.
//   • Friendly name columns for Account/Closest Resource + Primary/Billing contacts.
//   • Ensures new columns show up in chooser even with saved layouts.
//   • Uses RESOURCES (staff) for Account Manager & Closest Resource.
//   • Leftmost “+” expander shows a per-row ContactAccordion with that customer's contacts.
//   • Passes `onEdit` straight through to ContactAccordion so the Edit button launches
//     the same Edit Contact Modal logic used elsewhere.
//   • If `onEdit` is not provided by a parent, this widget now opens the canonical
//     EditContactModal itself, PORTALED to #modal-root, with all fields and dropdowns.
//
// Mobile Responsiveness (2026-09-07):
//   - Desktop table, drag/drop ordering, fetching, edit bridge, and persistence remain intact.
//   - Mobile reuses the same table rows as labelled customer cards; no duplicate mobile list is mounted.
//   - Mobile card height is measured from the actual first rendered customer record, so the current
//     Column Chooser field count/order plus Contacts and Edit rows determine the one-record viewport.
//   - Mobile Sort By + direction controls replace hidden table-header sorting on touch devices.
//   - Column Chooser adds touch-friendly up/down ordering while desktop header drag/drop remains unchanged.
//   - Collapsed records use one-card mandatory scroll snapping. When any customer ContactAccordion is
//     expanded, snapping is disabled so the expanded contact content can scroll naturally.

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axiosInstance from "../api/axiosInstance";
import { useDashboardRefresh } from "../context/DashboardRefreshContext";
import { filterAndSortList, getWidgetTitle } from "../utils/widgetUtils";
import ModalWrapper from "../components/ModalWrapper";
import ContactAccordion from "../components/ContactAccordion";
import EditContactModal from "../components/EditContactModal";

// ---------- Helpers ----------
const decodeJWT = (token) => {
  try {
    const [, payload] = token.split(".");
    return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return {};
  }
};

const getUserTenantKey = () => {
  const token =
    localStorage.getItem("anpr_token") || localStorage.getItem("token") || "";
  const p = decodeJWT(token) || {};
  const userId = p.sub || p.user_id || p.uid || "unknownUser";
  const tenantId = p.tenant_id || p.tid || "unknownTenant";
  return { userId, tenantId };
};

// ⬆️ Bump when column schema changes; missing columns are merged anyway.
const SCHEMA_VERSION = 2;
const storageKey = (userId, tenantId) =>
  `anpr_customer_columns_v${SCHEMA_VERSION}_${tenantId}_${userId}`;

// Column registry (all fields available)
const ALL_COLUMNS = [
  { key: "id", label: "ID" },
  { key: "name", label: "Name" },
  { key: "internal_reference", label: "Internal Ref" },
  { key: "nickname", label: "Nickname" },
  { key: "website", label: "Website" },
  { key: "email", label: "Email" },
  { key: "phone_number", label: "Phone" },
  { key: "status", label: "Status" },
  { key: "notes", label: "Notes" },
  { key: "address_line_1", label: "Address 1" },
  { key: "address_line_2", label: "Address 2" },
  { key: "address_line_3", label: "Address 3" },
  { key: "city", label: "City" },
  { key: "county", label: "County" },
  { key: "postcode", label: "Postcode" },
  { key: "country", label: "Country" },
  { key: "reg_address_line_1", label: "Reg. Address 1" },
  { key: "reg_address_line_2", label: "Reg. Address 2" },
  { key: "reg_address_line_3", label: "Reg. Address 3" },
  { key: "reg_city", label: "Reg. City" },
  { key: "reg_county", label: "Reg. County" },
  { key: "reg_postcode", label: "Reg. Postcode" },
  { key: "company_number", label: "Company No." },
  { key: "sic_code", label: "SIC Code" },
  { key: "nature_of_business", label: "Nature of Business" },
  { key: "formerly_known_as", label: "Formerly Known As" },
  { key: "customer_since", label: "Customer Since" },
  { key: "category", label: "Category" },

  // Raw IDs (kept for debugging/ops)
  { key: "account_manager_id", label: "Account Manager (ID)" },
  { key: "closest_resource_id", label: "Closest Resource (ID)" },
  { key: "parent_customer_id", label: "Parent Company (ID)" },
  { key: "primary_contact_id", label: "Primary Contact (ID)" },
  { key: "billing_contact_id", label: "Billing Contact (ID)" },

  // Friendly name columns (resolved on FE)
  { key: "account_manager_name", label: "Account Manager" }, // from resources
  { key: "closest_resource_name", label: "Closest Resource" }, // from resources
  { key: "primary_contact_name", label: "Primary Contact" }, // from contacts
  { key: "billing_contact_name", label: "Billing Contact" }, // from contacts
];

// Default visible + order
const DEFAULT_VISIBLE_ORDER = [
  "name",
  "internal_reference",
  "email",
  "phone_number",
  "status",
  "city",
  "postcode",
  "customer_since",
  "category",
];

// Search fields (include friendly names)
const SEARCH_FIELDS = [
  "name",
  "nickname",
  "internal_reference",
  "email",
  "phone_number",
  "status",
  "website",
  "city",
  "county",
  "postcode",
  "reg_city",
  "company_number",
  "sic_code",
  "category",
  "formerly_known_as",
  "nature_of_business",
  "account_manager_name",
  "closest_resource_name",
  "primary_contact_name",
  "billing_contact_name",
];

const getCell = (row, key) => {
  if (key === "customer_since") {
    const v = row?.customer_since;
    if (!v) return "";
    return String(v).slice(0, 10);
  }
  return row?.[key] ?? "";
};

// ---------- Column Chooser Modal (PORTALED) ----------
const ColumnChooserModal = ({
  open,
  onClose,
  allColumns,
  visibilityMap,
  setVisibilityMap,
  order,
  setOrder,
  resetToDefault,
}) => {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const handleToggle = (key) => {
    setVisibilityMap((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const moveColumn = (key, direction) => {
    setOrder((prev) => {
      const fromIndex = prev.indexOf(key);
      const toIndex = fromIndex + direction;
      if (fromIndex < 0 || toIndex < 0 || toIndex >= prev.length) return prev;
      const next = prev.slice();
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  const content = (
    <ModalWrapper onClose={onClose}>
      <h3 className="text-lg font-semibold mb-3">Choose Columns</h3>
      <p className="text-sm text-gray-600 mb-3">
        Tick the columns you want to see. Drag desktop table headers to reorder
        them, or use the mobile up/down controls below.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 max-h-[60vh] md:max-h-80 overflow-auto pr-1 md:pr-2">
        {order.map((key, index) => {
          const col = allColumns.find((c) => c.key === key);
          if (!col) return null;
          return (
            <div key={key} className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 p-2 md:border-0 md:rounded-none md:p-0">
              <label className="flex min-w-0 flex-1 items-center gap-3 text-sm">
                <input type="checkbox" checked={!!visibilityMap[key]} onChange={() => handleToggle(key)} className="h-5 w-5 shrink-0 md:h-auto md:w-auto" />
                <span className="truncate">{col.label}</span>
              </label>
              <div className="flex shrink-0 gap-1 md:hidden">
                <button type="button" onClick={() => moveColumn(key, -1)} disabled={index === 0} className="min-h-[40px] min-w-[40px] rounded border border-gray-300 bg-white px-2 text-base font-semibold text-gray-700 disabled:opacity-35" aria-label={`Move ${col.label} up`} title={`Move ${col.label} up`}>↑</button>
                <button type="button" onClick={() => moveColumn(key, 1)} disabled={index === order.length - 1} className="min-h-[40px] min-w-[40px] rounded border border-gray-300 bg-white px-2 text-base font-semibold text-gray-700 disabled:opacity-35" aria-label={`Move ${col.label} down`} title={`Move ${col.label} down`}>↓</button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
        <button type="button" onClick={resetToDefault} className="min-h-[44px] w-full bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white text-sm font-semibold py-2 px-3 rounded shadow sm:w-auto">Reset to Default</button>
        <button type="button" onClick={onClose} className="min-h-[44px] w-full bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick text-white text-sm font-semibold py-2 px-3 rounded shadow sm:w-auto">Done</button>
      </div>
    </ModalWrapper>
  );

  const portalTarget = document.getElementById("modal-root") || document.body;
  return createPortal(content, portalTarget);
};

// ---------- Main Widget ----------
const AdvancedCustomerListWidget = ({ onEditCustomer, onEdit }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState("name");
  const [ascending, setAscending] = useState(true);
  const { refreshKey } = useDashboardRefresh();

  // Name maps for friendly columns
  const [resourcesMap, setResourcesMap] = useState({});
  const [contactsMap, setContactsMap] = useState({});
  const [allContacts, setAllContacts] = useState([]); // full contact objects

  // Grouped contacts by customer_id for fast lookup
  const contactsByCustomer = useMemo(() => {
    const grouped = {};
    (allContacts || []).forEach((c) => {
      const cid = c.customer_id || 0;
      if (!grouped[cid]) grouped[cid] = [];
      grouped[cid].push(c);
    });
    return grouped;
  }, [allContacts]);

  // Track expanded rows by customer id
  const [expanded, setExpanded] = useState(() => ({}));

  const toggleRow = (customerId) => {
    setExpanded((prev) => ({ ...prev, [customerId]: !prev[customerId] }));
  };

  // Local Edit Contact modal state (uses canonical EditContactModal)
  const [editContact, setEditContact] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  // Column UI state
  const { userId, tenantId } = useMemo(getUserTenantKey, []);
  const persistKey = useMemo(
    () => storageKey(userId, tenantId),
    [userId, tenantId]
  );

  // Visibility map
  const [visibilityMap, setVisibilityMap] = useState(() => {
    const raw = localStorage.getItem(persistKey);
    let map = {};
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        map = parsed.visibilityMap || {};
      } catch {}
    }
    ALL_COLUMNS.forEach((c) => {
      if (typeof map[c.key] === "undefined") {
        map[c.key] = DEFAULT_VISIBLE_ORDER.includes(c.key);
      }
    });
    return map;
  });

  // Column order
  const [order, setOrder] = useState(() => {
    const raw = localStorage.getItem(persistKey);
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed.order) && parsed.order.length) {
          const saved = parsed.order;
          const missing = ALL_COLUMNS.map((c) => c.key).filter(
            (k) => !saved.includes(k)
          );
          return [...saved, ...missing];
        }
      } catch {}
    }
    const defaults = DEFAULT_VISIBLE_ORDER.slice();
    const rest = ALL_COLUMNS.map((c) => c.key).filter(
      (k) => !defaults.includes(k)
    );
    return [...defaults, ...rest];
  });

  // Column chooser
  const [chooserOpen, setChooserOpen] = useState(false);

  // Mobile one-record viewport sizing. Measure the actual rendered customer
  // card so the selected column count/order and wrapped content determine height.
  const widgetRootRef = useRef(null);
  const mobileHeaderRef = useRef(null);
  const recordsViewportRef = useRef(null);
  const firstCardRef = useRef(null);

  // Persist settings
  useEffect(() => {
    const data = { order, visibilityMap, v: SCHEMA_VERSION };
    localStorage.setItem(persistKey, JSON.stringify(data));
  }, [order, visibilityMap, persistKey]);

  // Fetch data (no trailing slash)
  useEffect(() => {
    setLoading(true);
    axiosInstance
      .get("customers?limit=1000")
      .then((res) => setCustomers(res.data || []))
      .catch((err) =>
        console.error(
          "❌ Failed to fetch customers:",
          err?.response?.data || err?.message || err
        )
      )
      .finally(() => setLoading(false));
  }, [refreshKey]);

  // Fetch RESOURCES & CONTACTS once to build name maps & contact lists
  useEffect(() => {
    let cancelled = false;

    const loadMaps = async () => {
      try {
        const [rRes, cRes] = await Promise.all([
          axiosInstance.get("resources/?limit=1000"),
          axiosInstance.get("contacts?limit=1000"),
        ]);

        if (cancelled) return;

        const rMap = {};
        (rRes.data || []).forEach((r) => {
          const name =
            [r.first_name, r.last_name].filter(Boolean).join(" ").trim() ||
            r.email ||
            `Resource #${r.id}`;
          rMap[r.id] = name;
        });

        const cMap = {};
        (cRes.data || []).forEach((c) => {
          const name =
            [c.first_name, c.last_name].filter(Boolean).join(" ").trim() ||
            c.email ||
            `Contact #${c.id}`;
          cMap[c.id] = name;
        });

        setResourcesMap(rMap);
        setContactsMap(cMap);
        setAllContacts(Array.isArray(cRes.data) ? cRes.data : []);
      } catch (e) {
        if (!cancelled) {
          console.warn(
            "⚠️ Failed to load resources/contacts for name mapping:",
            e?.response?.data || e?.message || e
          );
          setResourcesMap({});
          setContactsMap({});
          setAllContacts([]);
        }
      }
    };

    loadMaps();
    return () => {
      cancelled = true;
    };
  }, []);

  // Sorting
  const handleSort = (field) => {
    if (field === sortField) {
      setAscending((a) => !a);
    } else {
      setSortField(field);
      setAscending(true);
    }
  };

  // Drag & Drop headers
  const dragIndexRef = useRef(null);

  const visibleKeys = useMemo(
    () => order.filter((k) => visibilityMap[k]),
    [order, visibilityMap]
  );

  const onHeaderDragStart = (e, key) => {
    const idx = visibleKeys.indexOf(key);
    dragIndexRef.current = idx;
    e.dataTransfer.effectAllowed = "move";
  };

  const onHeaderDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const onHeaderDrop = (e, dropKey) => {
    e.preventDefault();
    const fromIdx = dragIndexRef.current;
    const toIdx = visibleKeys.indexOf(dropKey);
    if (
      fromIdx === null ||
      fromIdx === -1 ||
      toIdx === -1 ||
      fromIdx === toIdx
    )
      return;

    const newVisible = visibleKeys.slice();
    const [moved] = newVisible.splice(fromIdx, 1);
    newVisible.splice(toIdx, 0, moved);

    const hidden = order.filter((k) => !visibilityMap[k]);
    setOrder([...newVisible, ...hidden]);

    dragIndexRef.current = null;
  };

  const filteredCustomers = useMemo(
    () =>
      filterAndSortList(
        customers,
        searchQuery,
        SEARCH_FIELDS,
        sortField,
        ascending
      ),
    [customers, searchQuery, sortField, ascending]
  );

  const anyCustomerContactsExpanded = useMemo(
    () => Object.values(expanded).some(Boolean),
    [expanded]
  );

  useLayoutEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;

    const root = widgetRootRef.current;
    const header = mobileHeaderRef.current;
    const viewport = recordsViewportRef.current;
    if (!root || !header || !viewport) return undefined;

    const mediaQuery = window.matchMedia("(max-width: 767px)");
    let animationFrame = 0;
    let hostRecord = null;

    const restoreHost = () => {
      if (!hostRecord?.node) return;
      hostRecord.node.style.height = hostRecord.height;
      hostRecord.node.style.minHeight = hostRecord.minHeight;
      hostRecord = null;
    };

    const findFixedMobileHost = () => {
      const rootRect = root.getBoundingClientRect();
      const candidates = [];
      let node = root.parentElement;
      let depth = 0;
      while (node && node !== document.body && depth < 6) {
        const rect = node.getBoundingClientRect();
        const inlineHeight = String(node.style?.height || "").trim();
        const widthMatches = Math.abs(rect.width - rootRect.width) < 90;
        const hasFixedInlineHeight = inlineHeight && inlineHeight !== "auto" && inlineHeight !== "100%" && inlineHeight !== "100vh";
        if (widthMatches && hasFixedInlineHeight) candidates.push(node);
        node = node.parentElement;
        depth += 1;
      }
      return candidates.length ? candidates[0] : null;
    };

    const applyMobileSizing = () => {
      cancelAnimationFrame(animationFrame);
      restoreHost();
      viewport.style.height = "";
      viewport.style.flex = "";
      root.style.height = "";
      root.style.minHeight = "";
      if (!mediaQuery.matches) return;

      animationFrame = requestAnimationFrame(() => {
        const firstCard = firstCardRef.current;
        const cardHeight = firstCard ? Math.ceil(firstCard.getBoundingClientRect().height) : 120;
        const recordViewportHeight = cardHeight + 24;
        viewport.style.height = `${recordViewportHeight}px`;
        viewport.style.flex = "0 0 auto";
        root.style.height = "auto";
        root.style.minHeight = "0px";

        animationFrame = requestAnimationFrame(() => {
          const host = findFixedMobileHost();
          if (!host) return;
          hostRecord = { node: host, height: host.style.height, minHeight: host.style.minHeight };
          const hostRect = host.getBoundingClientRect();
          const rootRect = root.getBoundingClientRect();
          const wrapperChromeHeight = Math.max(0, rootRect.top - hostRect.top);
          const requiredHostHeight = Math.ceil(Math.max(host.scrollHeight, wrapperChromeHeight + rootRect.height + 8));
          host.style.height = `${requiredHostHeight}px`;
          host.style.minHeight = `${requiredHostHeight}px`;
        });
      });
    };

    applyMobileSizing();
    const resizeObserver = typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => applyMobileSizing()) : null;
    if (resizeObserver) {
      resizeObserver.observe(header);
      if (firstCardRef.current) resizeObserver.observe(firstCardRef.current);
    }
    const handleMediaChange = () => applyMobileSizing();
    mediaQuery.addEventListener?.("change", handleMediaChange);

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();
      mediaQuery.removeEventListener?.("change", handleMediaChange);
      restoreHost();
      viewport.style.height = "";
      viewport.style.flex = "";
      root.style.height = "";
      root.style.minHeight = "";
    };
  }, [filteredCustomers, visibleKeys]);

  const renderSortIndicator = (field) => {
    if (field !== sortField) return null;
    return ascending ? " ▲" : " ▼";
  };

  const resetToDefault = () => {
    const map = {};
    ALL_COLUMNS.forEach(
      (c) => (map[c.key] = DEFAULT_VISIBLE_ORDER.includes(c.key))
    );
    setVisibilityMap(map);
    const defaults = DEFAULT_VISIBLE_ORDER.slice();
    const rest = ALL_COLUMNS.map((c) => c.key).filter(
      (k) => !defaults.includes(k)
    );
    setOrder([...defaults, ...rest]);
    setSortField("name");
    setAscending(true);
  };

  // Local renderer that resolves friendly-name columns via maps.
  const renderCell = (row, key) => {
    if (key === "account_manager_name") {
      const id = row?.account_manager_id;
      return id ? resourcesMap[id] || `Resource #${id}` : "";
    }
    if (key === "closest_resource_name") {
      const id = row?.closest_resource_id;
      return id ? resourcesMap[id] || `Resource #${id}` : "";
    }
    if (key === "primary_contact_name") {
      const id = row?.primary_contact_id;
      return id ? contactsMap[id] || `Contact #${id}` : "";
    }
    if (key === "billing_contact_name") {
      const id = row?.billing_contact_id;
      return id ? contactsMap[id] || `Contact #${id}` : "";
    }
    return getCell(row, key);
  };

  // ---------- EDIT EVENT BRIDGE → EditContactModal ----------
  useEffect(() => {
    const handler = (evt) => {
      const contact = evt?.detail;
      if (!contact) return;

      // If parent supplied onEdit, respect that first.
      if (typeof onEdit === "function") {
        try {
          onEdit(contact);
        } catch (e) {
          console.error("[AdvancedCustomerListWidget] onEdit threw:", e);
        }
        return;
      }

      // Fallback: open canonical EditContactModal locally
      setEditContact(contact);
      setEditOpen(true);
    };

    const names = ["anpr:contacts:edit", "anpr:contact:edit", "anpr:editContact"];
    names.forEach((n) => window.addEventListener(n, handler));
    console.debug(
      "[AdvancedCustomerListWidget] edit bridge installed — listening for:",
      names.join(", "),
      "— onEdit is",
      typeof onEdit === "function" ? "function ✅" : String(onEdit)
    );

    return () => {
      names.forEach((n) => window.removeEventListener(n, handler));
    };
  }, [onEdit]);

  useEffect(() => {
    console.debug(
      "[AdvancedCustomerListWidget] mounted — onEdit is",
      typeof onEdit === "function" ? "function ✅" : String(onEdit)
    );
  }, [onEdit]);

  // Render expander cell content (button) — centered
  const renderExpanderCell = (customer) => {
    const list = contactsByCustomer[customer.id] || [];
    const hasContacts = list.length > 0;
    const isOpen = !!expanded[customer.id];

    const baseStyle = {
      color: hasContacts ? "var(--accent-color, #ff7a00)" : "#bdbdbd",
      cursor: hasContacts ? "pointer" : "not-allowed",
      opacity: hasContacts ? 1 : 0.6,
      width: 28,
      height: 28,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      borderRadius: 6,
      border: "1px solid rgba(0, 0, 0, 0.1)",
      background: hasContacts ? "rgba(0,0,0,0.02)" : "transparent",
      userSelect: "none",
    };

    return (
      <button
        type="button"
        aria-label={
          hasContacts
            ? isOpen
              ? "Collapse contacts"
              : "Expand contacts"
            : "No contacts"
        }
        title={
          hasContacts
            ? isOpen
              ? "Hide contacts"
              : "Show contacts"
            : "No contacts available"
        }
        onClick={() => hasContacts && toggleRow(customer.id)}
        disabled={!hasContacts}
        style={baseStyle}
      >
        {isOpen ? "−" : "+"}
      </button>
    );
  };

  return (
    <div ref={widgetRootRef} className="h-auto min-h-0 w-full overflow-hidden rounded-lg border border-gray-200 shadow-sm bg-white flex flex-col md:h-full">
      <div ref={mobileHeaderRef} className="bg-accent text-white px-3 py-3 md:px-4 md:py-2 flex flex-col gap-3 md:flex-row md:flex-wrap md:justify-between md:items-center">
        <span className="text-sm font-semibold">Search the {getWidgetTitle("advanced_customer_list")}</span>

        <div className="grid w-full grid-cols-[minmax(0,1fr)_auto] gap-2 md:flex md:w-auto md:items-center">
          <input type="text" placeholder="Search..." className="min-h-[44px] min-w-0 w-full rounded border border-white bg-white px-3 py-2 text-sm text-black md:min-h-0 md:w-48 md:flex-none md:px-2 md:py-1" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
          <button type="button" onClick={() => setChooserOpen(true)} className="min-h-[44px] whitespace-nowrap bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white text-xs font-semibold px-3 py-2 rounded shadow md:min-h-0 md:py-1" title="Choose Columns">Column Chooser</button>
        </div>

        <div className="grid w-full grid-cols-[minmax(0,1fr)_auto] gap-2 md:hidden">
          <label className="flex min-w-0 flex-col gap-1 text-xs font-semibold">
            Sort by
            <select value={sortField} onChange={(event) => setSortField(event.target.value)} className="min-h-[44px] w-full rounded border border-white bg-white px-3 py-2 text-sm font-normal text-black">
              {ALL_COLUMNS.map((column) => <option key={column.key} value={column.key}>{column.label}</option>)}
            </select>
          </label>
          <button type="button" onClick={() => setAscending((current) => !current)} className="min-h-[44px] self-end rounded bg-btnGeneralNormal px-4 py-2 text-sm font-semibold text-white shadow hover:bg-btnGeneralHover active:bg-btnGeneralClick" aria-label={ascending ? "Sort descending" : "Sort ascending"} title={ascending ? "Currently ascending — tap for descending" : "Currently descending — tap for ascending"}>{ascending ? "▲" : "▼"}</button>
        </div>
      </div>

      <div ref={recordsViewportRef} className={`min-h-0 w-full overflow-y-auto overflow-x-hidden overscroll-contain scroll-smooth md:flex-1 md:overflow-auto ${anyCustomerContactsExpanded ? "snap-none" : "snap-y snap-mandatory md:snap-none"}`}>
        <table className="block w-full text-left text-sm text-gray-700 md:table">
          <thead className="hidden md:table-header-group">
            <tr className="bg-accent text-white select-none">
              <th className="px-2 py-2 text-center w-10 sticky top-0 z-10 bg-accent">+</th>
              {visibleKeys.map((key) => {
                const col = ALL_COLUMNS.find((c) => c.key === key);
                if (!col) return null;
                return <th key={key} className="px-4 py-2 cursor-move sticky top-0 z-10 bg-accent" draggable onDragStart={(e) => onHeaderDragStart(e, key)} onDragOver={onHeaderDragOver} onDrop={(e) => onHeaderDrop(e, key)} onClick={() => handleSort(key)} title="Drag to reorder • Click to sort">{col.label}{renderSortIndicator(key)}</th>;
              })}
              <th className="px-4 py-2 text-center sticky top-0 z-10 bg-accent">Actions</th>
            </tr>
          </thead>

          <tbody className="block space-y-3 p-3 md:table-row-group md:space-y-0 md:p-0">
            {loading ? (
              <tr className="block md:table-row"><td colSpan={visibleKeys.length + 2} className="block rounded-lg border border-gray-200 bg-white px-4 py-5 text-center text-gray-500 italic md:table-cell md:rounded-none md:border-0">Loading customer records...</td></tr>
            ) : filteredCustomers.length === 0 ? (
              <tr className="block md:table-row"><td colSpan={visibleKeys.length + 2} className="block rounded-lg border border-gray-200 bg-white px-4 py-5 text-center text-gray-500 italic md:table-cell md:rounded-none md:border-0">No customers found.</td></tr>
            ) : (
              filteredCustomers.map((customer, index) => {
                const hasContacts = (contactsByCustomer[customer.id] || []).length > 0;
                const isOpen = !!expanded[customer.id];
                return (
                  <React.Fragment key={customer.id}>
                    <tr ref={index === 0 ? firstCardRef : undefined} className="block snap-start scroll-mt-3 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm md:table-row md:rounded-none md:border-x-0 md:border-t-0 md:border-b md:border-gray-100 md:shadow-none md:align-top md:odd:bg-gray-50 md:even:bg-white">
                      <td className="flex items-center justify-between gap-4 border-b border-gray-100 px-4 py-3 md:table-cell md:border-b-0 md:px-2 md:py-2 md:text-center md:align-middle">
                        <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-gray-500 md:hidden">Customer Contacts</span>
                        {renderExpanderCell(customer)}
                      </td>

                      {visibleKeys.map((key) => {
                        const col = ALL_COLUMNS.find((column) => column.key === key);
                        if (!col) return null;
                        return (
                          <td key={key} className="flex items-start justify-between gap-4 border-b border-gray-100 px-4 py-3 md:table-cell md:border-b-0 md:px-4 md:py-2 md:align-top">
                            <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-gray-500 md:hidden">{col.label}</span>
                            <span className="min-w-0 break-words text-right md:text-left">{renderCell(customer, key)}</span>
                          </td>
                        );
                      })}

                      <td className="block px-4 py-3 md:table-cell md:px-4 md:py-2 md:text-center md:align-top">
                        <button type="button" onClick={() => onEditCustomer?.(customer)} className="min-h-[44px] w-full bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick text-white text-sm font-body px-3 py-2 rounded shadow transition-colors duration-200 ease-in-out md:min-h-0 md:w-auto md:text-xs md:py-1" title="Edit Customer">Edit</button>
                      </td>
                    </tr>

                    {isOpen && hasContacts && (
                      <tr className="block bg-white md:table-row">
                        <td colSpan={visibleKeys.length + 2} className="block rounded-lg border border-gray-200 bg-white p-3 shadow-sm md:table-cell md:rounded-none md:border-0 md:px-4 md:pb-4 md:pt-0 md:shadow-none">
                          <ContactAccordion customerId={customer.id} customerName={customer.name || `Customer #${customer.id}`} contacts={contactsByCustomer[customer.id] || []} startOpen={true} onEdit={onEdit} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <ColumnChooserModal open={chooserOpen} onClose={() => setChooserOpen(false)} allColumns={ALL_COLUMNS} visibilityMap={visibilityMap} setVisibilityMap={setVisibilityMap} order={order} setOrder={setOrder} resetToDefault={resetToDefault} />

      {editOpen && editContact && createPortal(
        <EditContactModal contact={editContact} onClose={() => setEditOpen(false)} />,
        document.getElementById("modal-root") || document.body
      )}
    </div>
  );
};

export default AdvancedCustomerListWidget;
